/* 动画 item 属性选择项 */
import React, { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import NumberSlideBar, { NumberSlideBarProps } from '@frame/components/property-editor/editor-components/number-slidebar';
import { ISelectProps } from '@tencent/dui/lib/components/Select/interface';
import Select from '@tencent/dui/lib/components/Select';
import AnimColorPicker from '../../anim-color-picker';
import type { IProps as ColorPickerProps } from '../../anim-color-picker';

import Style from '../index.less';

type ValueType = 'select'  // 下拉选项
    | 'color' // 颜色选择
    | 'numberSlideBar'; // 数字滑动条

type TOption = {
    key: string,
    label: ReactNode,
    value: string,
    style?: CSSProperties
};

export type AnimItemPropertyProps = {
    // 属性名称
    title: string;
    // 是否隐藏
    hidden?: boolean;
    // 属性类型，暂时有下拉选项和数字滑动条
    valueType: ValueType
    // 自定义样式
    style?: CSSProperties,
    // 自定义 class
    className?: string;
    // select 类型组件属性覆盖
    selectProps?: Partial<ISelectProps & {
        options: TOption[];
        // 自定义 Select.Option 渲染内容
        customOptionEl?: (props: TOption) => ReactNode;
    }>;
    // numberSlideBar 类型组件属性覆盖
    numberSlidebarProps?: Partial<NumberSlideBarProps>
    // colorPicker 类型组件属性覆盖
    colorPickerProps?: ColorPickerProps;
    // 属性修改事件
    onPropertyChange?: (value: string | number, changing?: boolean) => void
};

const AnimItemProperty = (props: AnimItemPropertyProps) => {
    const {
        title,
        valueType,
        hidden,
        style,
        className,
        selectProps,
        numberSlidebarProps,
        colorPickerProps,
        onPropertyChange,
    } = props;

    const { options = [], customOptionEl } = selectProps || {};

    return (
        <div className={classNames(className, Style.item, {
            [Style['item-hidden']]: hidden,
        })} style={style}>
            <span className={Style.itemTitle} >{title}</span>
            {valueType === 'numberSlideBar'
                ? <AnimPropertyNumberSlideBar
                    {...numberSlidebarProps}
                    onModify={onPropertyChange}
                /> : null}
            { valueType === 'color'
                ? <AnimColorPicker {...colorPickerProps as ColorPickerProps}/>
                : null}
            {valueType === 'select'
                ? <Select
                    className={Style['animation-item-select']}
                    dropdownAlignment="right"
                    dropdownMatchSelectWidth={true}
                    {...selectProps}
                    listStyle={{
                        boxSizing: 'content-box',
                        height: 'auto',
                        ...selectProps?.listStyle,
                    }}
                    textStyle={{
                        fontSize: 12,
                        cursor: 'default',
                        ...selectProps?.textStyle,
                    }}
                    onChange={onPropertyChange}
                >
                    {
                        options?.map((option) => {
                            const { key, label, value, style: optionStyle } = option;
                            if (customOptionEl) {
                                return customOptionEl(option);
                            }

                            return <Select.Option
                                key={`${title}-${key}`}
                                value={value}
                                style={optionStyle}
                            >
                                {label}
                            </Select.Option>;
                        })
                    }
                </Select> : null}
        </div>
    );
};

type AnimPropertyNumberSlideBarProps = Partial<NumberSlideBarProps> & {
    onModify?: (value, changing?: boolean) => void
};

const AnimPropertyNumberSlideBar = (props: AnimPropertyNumberSlideBarProps) => {
    const { value, onValueEnter, onValueLeave, onModify } = props;
    const [property, setProperty] = useState(value);
    const focused = useRef<boolean>(false);

    const handleBlur = () => {
        focused.current = false;
        const value = parseFloat(String(property));
        const propertyValue = isNaN(value) ? 0 : value;
        // NumberSlideBar 组件中 onValueChange 事件在 onBlur 事件之前触发，此处需要调用 onModify
        if (props.value !== propertyValue) {
            onModify?.(value);
        }
        setProperty(propertyValue);
    };

    const handleFocus = () => {
        focused.current = true;
        const value = parseFloat(String(property));
        setProperty(value);
    };

    const handleValueChange = (type, value, fromCustom, changing) => {
        setProperty(value);
        const needModify = !(changing || focused.current);
        if (needModify) {
            // modify 和 tdw 上报交给外部
            onModify?.(value, changing);
        }
    };

    const handleValueEnter = (e) => {
        onValueEnter?.(e);
    };
    const handleValueLeave = (e) => {
        onValueLeave?.(e);
    };

    useEffect(() => {
        setProperty(value);
    }, [value]);

    return (
        <NumberSlideBar
            title={''}
            toFixed={0}
            slideBarStep={5}
            numberInputStep={5}
            lowerLimit={0}
            upperLimit={100}
            postFix={'%'}
            size={'small'}
            type={'property'}
            canShowEmptyString={true}
            // ⬆️️ 使用强调动画透明度的默认值，其他使用外部 props 覆盖
            onBlur={handleBlur}
            onFocus={handleFocus}
            onValueChange={handleValueChange}
            onValueEnter={handleValueEnter}
            onValueLeave={handleValueLeave}
            {...props}
            value={property}
        />
    );
};

export default AnimItemProperty;
